import React, {FC, useEffect, useRef, useState} from 'react'
import {useDriverChat} from '@containers/chat/hooks/useDriverChat'
import {ChatMessage, getMessageTypeMeta, MESSAGE_TYPES} from '@containers/chat/types'

interface Props {
    driverId: number
    driverName?: string | null
    plate?: string
}

const formatTime = (date: string | number): string => {
    const d = new Date(typeof date === 'number' ? date : String(date))
    if (Number.isNaN(d.getTime())) {
        return ''
    }
    return d.toLocaleString('uk-UA', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})
}

const isOwn = (m: ChatMessage) => String(m.sender).toUpperCase() === 'DISPATCHER'

// Delivery ticks as in the legacy view: SENT one tick, DELIVERED two, READ two in blue.
const StatusTicks: FC<{status?: string}> = ({status}) => {
    if (!status) {
        return null
    }
    const read = status === 'READ'
    return (
        <span className={`vs-chat__ticks${read ? ' vs-chat__ticks--read' : ''}`} title={status}>
            {status === 'SENT' ? '✓' : '✓✓'}
        </span>
    )
}

/**
 * Reusable driver chat thread: used inside the global chat modal and anywhere else.
 * Message types (icon plus colour coding), delivery statuses, incremental polling,
 * a sound for incoming messages, auto-scroll and sending with a chosen type.
 */
const DriverChatThread: FC<Props> = ({driverId, driverName, plate}) => {
    const {messages, loading, sending, send} = useDriverChat(driverId, true)
    const [text, setText] = useState('')
    const [type, setType] = useState<string>('TEXT')
    const listRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = listRef.current
        if (el) {
            el.scrollTop = el.scrollHeight
        }
    }, [messages.length])

    const submit = () => {
        const value = text.trim()
        if (!value || sending) {
            return
        }
        void send(value, type)
        setText('')
        setType('TEXT')
    }

    return (
        <div className="vs-chat__thread">
            {(driverName || plate) && (
                <div className="vs-chat__sub">
                    {driverName || `Водій #${driverId}`}{plate ? ` · ${plate}` : ''}
                </div>
            )}

            <div className="vs-chat__list" ref={listRef}>
                {loading && messages.length === 0 ? (
                    <div className="vs-chat__empty">Завантаження…</div>
                ) : messages.length === 0 ? (
                    <div className="vs-chat__empty">Повідомлень ще немає</div>
                ) : (
                    messages.map((m) => {
                        const meta = getMessageTypeMeta(m.messageType)
                        return (
                            <div
                                key={m.serverId || m.id || m.clientMessageId}
                                className={`vs-chat__msg${isOwn(m) ? ' vs-chat__msg--own' : ''}`}
                            >
                                <div className={`vs-chat__bubble vs-chat__bubble--${meta.severity}`}>
                                    {meta.icon && (
                                        <span className="vs-chat__type" title={meta.label}>{meta.icon}</span>
                                    )}
                                    <span className="vs-chat__text">{m.message}</span>
                                </div>
                                <div className="vs-chat__meta">
                                    {formatTime(m.date)}
                                    {isOwn(m) && <StatusTicks status={m.status}/>}
                                </div>
                            </div>
                        )
                    })
                )}
            </div>

            <div className="vs-chat__input">
                <select
                    className="vs-chat__typeselect"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    title="Тип повідомлення"
                >
                    {MESSAGE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.icon ? `${t.icon} ` : ''}{t.label}</option>
                    ))}
                </select>
                <textarea
                    className="vs-chat__field"
                    placeholder="Повідомлення водієві…"
                    value={text}
                    rows={2}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            submit()
                        }
                    }}
                />
                <button
                    type="button"
                    className="vs-chat__send"
                    disabled={!text.trim() || sending}
                    onClick={submit}
                >
                    Надіслати
                </button>
            </div>
        </div>
    )
}

export default DriverChatThread